import { pauseTracking, resetTracking } from "@vue/reactivity";
import { type Ref, toValue } from "vue";

const debounce = <P extends any[]>(
  func: (...args: P) => void,
  wait: number,
  immediate = false
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: P | null = null;
  let lastThis: any = null;

  const invoke = () => {
    const args = lastArgs!;
    const that = lastThis;
    lastArgs = null;
    lastThis = null;
    timeout = null;

    func.apply(that, args);
  };

  const debounced = function (this: any, ...args: P) {
    const callNow = immediate && !timeout;

    lastArgs = args;
    lastThis = this;

    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        invoke();
      }
    }, wait);

    if (callNow) {
      invoke();
    }
  };

  debounced.now = function (this: any, ...args: P) {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    lastArgs = args;
    lastThis = this;
    invoke();
  };

  debounced.abort = function () {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }

    lastArgs = null;
    lastThis = null;
  };

  return debounced;
};

const throttle = <P extends any[]>(
  func: (...args: P) => void,
  wait: number,
  leading = true
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  let lastArgs: P | null = null;
  let lastCallTime = 0;

  const invoke = () => {
    const args = lastArgs!;
    lastArgs = null;
    timeout = null;

    if (leading) {
      lastCallTime = Date.now();
    }

    func(...args);
  };

  const throttled = (...args: P) => {
    lastArgs = args;

    if (leading) {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;

      if (timeSinceLastCall >= wait) {
        if (timeout !== null) {
          clearTimeout(timeout);
        }

        invoke();
      } else if (timeout === null) {
        timeout = setTimeout(invoke, wait - timeSinceLastCall);
      }
    } else {
      if (timeout === null) {
        timeout = setTimeout(invoke, wait);
      }
    }
  };

  throttled.now = (...args: P) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    lastArgs = args;
    invoke();
  };
  throttled.abort = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return throttled;
};

// https://stackoverflow.com/questions/8667070/javascript-regular-expression-to-validate-url
const VALID_HREF_REGEXES = [
  /mailto:([^]*)/,
  /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i,
  /{{.*}}/,
];
const isValidHref = (url: string): boolean =>
  VALID_HREF_REGEXES.filter((r) => r.test(url)).length > 0;

// Given a hex string, e.g. #0069FF
const getTextColorForBackground = (hex: string) => {
  // Remove the #, leaving you with 0069FF.
  const rawHex = hex.replace("#", "");

  // Each two characters represents a color channel in the RGB space:
  // - R = 00
  // - G = 69
  // - B = FF
  const parseHex = (startPosition: number) =>
    parseInt(rawHex.substr(startPosition, 2), 16);

  // So we extract those channels.
  const [r, g, b] = [0, 2, 4].map(parseHex);

  // Convert theme using a YIQ formula:
  // https://en.wikipedia.org/wiki/YIQ#From_RGB_to_YIQ
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // YIQ space is between 0 and 255:
  // If its above average (> 128), then it's light, so we want dark text.
  // If its below average (< 128), then it's dark, so we want light text.
  return yiq >= 128 ? "black" : "white";
};

const fetchCookie = (key: string): string | null => {
  return document.cookie.split(/; */).reduce((obj, str) => {
    if (str === "") return obj;
    const eq = str.indexOf("=");
    const key = eq > 0 ? str.slice(0, eq) : str;
    let val = eq > 0 ? str.slice(eq + 1) : null;
    if (val != null)
      try {
        val = decodeURIComponent(val);
      } catch {
        /* pass */
      }
    obj[key] = val;
    return obj;
  }, {} as { [key: string]: string | null })[key];
};

const diffTwoObjects = <A>(a: A, b: A, relevantKeys: (keyof A)[]) => {
  const diff: {
    [key in keyof A]?: A[key];
  } = {};
  relevantKeys.forEach((key) => {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      diff[key] = b[key];
    }
  });
  return diff;
};

/**
 * Checks if an array contains at least one valid non-empty string.
 * @example
 * containsValidString(["", ""]); // false
 * containsValidString([""]); // false
 * containsValidString(["", "valid"]); // true
 */
const containsValidString = (arr: string[]): boolean => {
  return arr.some(
    (answer) => typeof answer === "string" && answer.trim() !== ""
  );
};

/**
 * Removes empty values from an array of strings.
 * @example
 * removeEmptyValuesFromArray(["", "hello", "", "world"]); // Output: ["hello", "world"]
 */
const removeEmptyValuesFromArray = (arr: string[]): string[] => {
  return arr.filter((value) => value.trim() !== "");
};

/**
 * returns an array with elements unique by a selector's return value
 * @param array the array to process
 * @param selector function to derive uniqueness keys
 * @returns a new array with elements unique based on the selector's key
 */
const uniqueBy = <T, K>(
  array: T[],
  selector: (value: T, index: number) => K
): T[] => {
  const keys = new Set<K>();
  const values: T[] = [];

  for (let idx = 0, len = array.length; idx < len; idx++) {
    const value = array[idx];
    const key = selector(value, idx);

    if (keys.has(key)) {
      continue;
    }

    keys.add(key);
    values.push(value);
  }

  return values;
};

/**
 * get a random integer between min (inclusive) and max (inclusive)
 * @param min minimum value
 * @param max maximum value
 * @returns random integer
 */
const getRandomIntInclusive = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

/**
 * grabs a random sample of items
 * @param arr array of items to sample from
 * @param n amount of items to sample
 * @returns sample
 */
const sample = <T>(arr: T[], n: number): T[] => {
  const result = [...arr];
  const len = result.length;

  n = Math.min(n, len);

  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (len - i));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return len > n ? result.slice(0, Math.max(0, n)) : result;
};

/**
 * access ref values without marking the ref as a watcher dependency
 * @param refOrGetter ref or getter accessing a ref
 * @returns the value of ref
 */
const untrack: {
  <T>(ref: Ref<T>): T;
  <T>(getter: () => T): T;
} = (refOrGetter: Ref<any> | (() => any)): any => {
  try {
    pauseTracking();
    return toValue(refOrGetter);
  } finally {
    resetTracking();
  }
};

/**
 * truncates excess text
 * @param text text to truncate
 * @param max maximum amount of characters
 * @returns truncated version of the text, if reached the max length
 */
const truncateRight = (text: string, max: number): string => {
  const len = text.length;

  if (len <= max) {
    return text;
  }

  return text.slice(0, max - 1).trimEnd() + "…";
};

const slugify = (text: string): string => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, "")
    .replace(/\-+/g, "-")
    .replace(/^\-|\-$/g, "");
};

export {
  containsValidString,
  debounce,
  diffTwoObjects,
  fetchCookie,
  getRandomIntInclusive,
  getTextColorForBackground,
  isValidHref,
  removeEmptyValuesFromArray,
  sample,
  slugify,
  throttle,
  truncateRight,
  uniqueBy,
  untrack,
};
